'use client';

import { supabaseClient } from '@/lib/_utils/supabase/client';
import { useEffect, useState } from 'react';
import AuthBlock from '@/components/authBlock/AuthBlock.tsx';
import useTranslation from 'next-translate/useTranslation';
import Link from 'next/link';
import Layout from '@/components/layout';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import useValidator from '@/hooks/validator.hook';
import { Button } from '@/components/button/Button';
import { useSearchParams } from 'next/navigation';
import useToast from '@/hooks/toast.hook';
import { Icon } from '@/components/icon/Icon';
import { regexPassword, regexUsername } from '@/lib/_utils';
import { Translate } from 'next-translate';
import { routeInfo } from '@/lib/_utils/routing.ts';
import { Countries } from '@/lib/auth/kyc/_utils/interfaces';
import Spinner from '@/components/spinner/Spinner';
import Block from '@/components/block/Block.tsx';
import EmailSearch from '@public/svg/email-search.svg';
import TrustpilotImg from '@public/svg/trustpilot.webp';
import TradingViewImg from '@public/svg/tradingview.webp';
import Image from 'next/image';
import countriesInfoList from '../../../../../../../packages/frontend/public/json/countries-info.json';
import { tradingViewPath, trustpilotTralentPath } from '@/lib/_utils/external-routing.ts';

interface SignUpFormType {
	f_name: string;
	l_name: string;
	phone_number_country_prefix: string;
	phone_number: string;
	country: string;
	username: string;
	email: string;
	password: string;
	referrer_code?: string;
	privacy?: boolean;
	terms?: boolean;
}

interface SignUpDataType {
	f_name: string;
	l_name: string;
	phone_number_country_prefix: string;
	phone_number: string;
	country: string;
	username: string;
	referrer_code?: string;
	is_major: boolean;
}

// Schema Yup
const schema = (t: Translate) => {
	return yup.object().shape({
		f_name: yup.string().required(),
		l_name: yup.string().required(),
		phone_number_country_prefix: yup.string().required(),
		phone_number: yup.string().min(6).max(12).required(),
		country: yup.string().required(),
		username: yup.string().required().matches(regexUsername, t('validations:invalid_username')),
		email: yup.string().required().email(),
		password: yup.string().required().matches(regexPassword, t('validations:invalid_password')),
		referrer_code: yup.string().optional().max(40),
		privacy: yup.bool().isTrue(t('validations:required', { field: t('fields:privacy') })),
		terms: yup.bool().isTrue(t('validations:required', { field: t('fields:terms') })),
	});
};

export default function SignupPageClient() {
	const searchParams = useSearchParams();
	const { t } = useTranslation('common');

	const [isLoadingForm, setIsLoadingForm] = useState<boolean>(false);
	const [isLoadingCountries, setIsLoadingCountries] = useState<boolean>(false);
	const [isMailConfirmShowed, setIsMailConfirmShowed] = useState<boolean>(false);
	const [countriesList, setCountriesList] = useState<Countries[] | null>([]);
	const [showPassword, setShowPassword] = useState(false);
	const { openToast } = useToast();

	const countriesMap = new Map(countriesInfoList.map((country) => [country.iso3, country]));
	const refCode = searchParams.get('ref');

	const {
		register,
		setValue,
		watch,
		handleSubmit,
		formState: { errors },
	} = useForm({ resolver: yupResolver(schema(t)) });

	useValidator(errors);

	const getCountries = async () => {
		setIsLoadingCountries(true);
		const { data: countries, error } = await supabaseClient.from('countries').select('iso3, name').eq('enabled', true);
		if (error) {
			openToast({
				text: t('validations:general'),
				fields: [error.message],
				status: 'danger',
				time: 3000,
			});
		} else {
			setCountriesList(countries);
			setIsLoadingCountries(false);
		}
	};

	const handleSignUp = async (params: SignUpFormType) => {
		if (!isLoadingForm) {
			setIsLoadingForm(true);

			const {
				email,
				password,
				referrer_code,
				f_name,
				l_name,
				phone_number_country_prefix,
				phone_number,
				country,
				username,
				terms,
			} = params;

			const data: SignUpDataType = {
				f_name,
				l_name,
				username,
				country,
				phone_number_country_prefix,
				phone_number,
				is_major: terms || false,
				...(referrer_code?.length && { referrer_code }),
			};

			const { error } = await supabaseClient.auth.signUp({
				email,
				password,
				options: {
					emailRedirectTo: `${location.origin}/auth/callback`,
					data,
				},
			});

			if (error) {
				openToast({
					text: t('validations:general'),
					fields: [error.message],
					status: 'danger',
					time: 3000,
				});
			} else {
				openToast({
					text: t('common:success'),
					fields: [t('common:check_email')],
					status: 'success',
					time: 3000,
				});

				setIsMailConfirmShowed(true);
			}
			setIsLoadingForm(false);
		}
	};

	useEffect(() => {
		if (refCode) setValue('referrer_code', refCode);
	}, [refCode]);

	useEffect(() => {
		if (watch('country') && !watch('phone_number_country_prefix')) {
			const countryData = countriesMap.get(watch('country'));
			if (countryData && countryData?.dial_code) {
				setValue('phone_number_country_prefix', countryData?.dial_code);
			}
		}
	}, [watch('country')]);

	useEffect(() => {
		getCountries();
	}, []);

	return (
		<Layout
			responsive
			style={{ global: 'bg_square' }}
			mainProps={{ className: 'flex items-center justify-center flex-1' }}
			gtmEventTrackingProps={{ event: 'pageview_event', var_pagetype: 'auth_signup' }}>
			<div className={`align-center flex h-full flex-col justify-center gap-5xl p-[30px] sm:p-[24px]`}>
				{!isMailConfirmShowed ? (
					<>
						<AuthBlock title={t('_signup.title')} subtitle={t('_signup.subtitle')}>
							<form onSubmit={handleSubmit((e) => handleSignUp(e))} className={`form-content`}>
								<div className={`item-row multi-col`}>
									<div className={`form-field floating-placeholder`}>
										<input
											type='text'
											placeholder={t('fields:f_name')}
											{...register('f_name')}
											className={`${errors.f_name ? 'err' : ''}`}
										/>
										<div className={'placeholder'}>{t('fields:f_name')}</div>
									</div>
									<div className={`form-field floating-placeholder`}>
										<input
											type='text'
											placeholder={t('fields:l_name')}
											{...register('l_name')}
											className={`${errors.l_name ? 'err' : ''}`}
										/>
										<div className={'placeholder'}>{t('fields:l_name')}</div>
									</div>
								</div>
								<div className={`item-row`}>
									<div className={`form-field floating-placeholder form-field-icon-right`}>
										<div className={`icon-right pointer-events-none`}>
											{isLoadingCountries ? <Spinner variant={'white'} /> : <Icon name='chevronDown' variant='white' />}
										</div>
										<select
											required
											defaultValue={''}
											{...register('country')}
											disabled={isLoadingCountries}
											className={register('country') ? '' : ''}>
											<option value='' disabled>
												{t('select_a_country')}
											</option>
											{countriesList?.map((item: Countries, itemIndex) => (
												<option value={item.iso3} key={itemIndex} className=''>
													{item.name}
												</option>
											))}
										</select>
										<div className='placeholder'>{t('country')}</div>
									</div>
								</div>
								<div className={`item-row multi-col grid-cols-2`}>
									<div className={`form-field floating-placeholder form-field-icon-right w-full`}>
										<div className={`icon-right pointer-events-none`}>
											<Icon name='chevronDown' variant='white' />
										</div>
										<select
											required
											defaultValue={''}
											{...register('phone_number_country_prefix')}
											className={`truncate ${register('phone_number_country_prefix') ? '' : ''}`}>
											<option value='' disabled>
												{t('select_dialing_code')}
											</option>

											{countriesInfoList.map((item, itemIndex) => (
												<option value={item.dial_code} key={itemIndex} className=''>
													{item.dial_code} · {item.emoji} {item.name}
												</option>
											))}
										</select>
										<div className='placeholder'>{t('fields:phone_number_country_prefix')}</div>
									</div>
									<div className={`form-field floating-placeholder`}>
										<input
											type='text'
											placeholder={t('fields:phone_number')}
											{...register('phone_number')}
											className={`${errors.phone_number ? 'err' : ''}`}
										/>
										<div className={'placeholder'}>{t('fields:phone_number')}</div>
									</div>
								</div>
								<div className={`item-row`}>
									<div className={`form-field floating-placeholder`}>
										<input
											type='email'
											placeholder={t('fields:email')}
											{...register('email')}
											className={`${errors.email ? 'err' : ''}`}
											readOnly={isLoadingForm}
										/>
										<div className={'placeholder'}>{t('fields:email')}</div>
									</div>
								</div>
								<div className={`item-row`}>
									<div
										className={`form-field form-field-icon-right floating-placeholder ${errors.password ? 'field-err' : ''}`}>
										<div className='mb-2'>
											<div className={`form-field floating-placeholder`}>
												<input
													type={showPassword ? 'text' : 'password'}
													placeholder={t('password')}
													{...register('password')}
													className={`${errors.password ? 'err' : ''}`}
												/>
												<div className={'placeholder'}>{t('password')}</div>
												<div
													className={`icon-right mx-2 cursor-pointer ${showPassword ? '' : ''}`}
													onClick={() => setShowPassword(!showPassword)}>
													{!showPassword ? <Icon name='eyeOpenOutlined' /> : <Icon name='eyeClosedOutlined' />}
												</div>
											</div>
										</div>
										<div className='flex'>
											<span className={`flex items-center gap-1 text-[12px] opacity-60`}>
												<Icon name='infoCircleOutlineIcon' size='sm' />
												{t('common:_signup.password_regex')}
											</span>
										</div>
									</div>
								</div>
								<div className={`item-row`}>
									<div className={`form-field floating-placeholder`}>
										<input
											type='text'
											placeholder={t('fields:username')}
											{...register('username')}
											className={`${errors.username ? 'err' : ''}`}
										/>
										<div className={'placeholder'}>{t('fields:username')}</div>
									</div>
								</div>
								<div className={`item-row`}>
									<div className={`checkbox flex ${errors.privacy ? 'err' : ''}`}>
										<input id='privacy' type='checkbox' {...register('privacy')} />
										<label htmlFor='privacy'>
											<div className='icon-checked'>
												<Icon name='checkIcon' size='sm' variant='primary' />
											</div>
											<span
												dangerouslySetInnerHTML={{
													__html: t('common:i_agree_{{link}}', {
														link: `<a href="${
															routeInfo({
																t,
																page: 'privacy',
															}).path
														}" target="_blank" rel='noopener noreferrer' class="link">${
															routeInfo({
																t,
																page: 'privacy',
															}).label
														} ›</a>`,
													}),
												}}
											/>
										</label>
									</div>
								</div>
								<div className={`item-row`}>
									<div className={`checkbox flex ${errors.terms ? 'err' : ''}`}>
										<input id='terms' type='checkbox' {...register('terms')} />
										<label htmlFor='terms'>
											<div className='icon-checked'>
												<Icon name='checkIcon' size='sm' variant='primary' />
											</div>
											<div
												dangerouslySetInnerHTML={{
													__html: t('common:_signup.terms_conditions', {
														terms_link: `<a
														href="${routeInfo({ t, page: 'tos' }).path}"
														target='_blank'
														rel='noopener noreferrer'
														class='link'>
														${t('common:terms_service')} ›
													</a>`,
													}),
												}}></div>
										</label>
									</div>
								</div>
								<div className={`grid gap-[20px]`}>
									<Button type='submit' variant={'primary'} loading={isLoadingForm}>
										{t('sign_up')}
									</Button>
									<div className='flex'>
										<span className={`mr-[6px] font-medium`}>{t('common:already_have_account')}</span>
										<Link href={routeInfo({ t, page: 'signin' }).path} className={`link`}>
											{routeInfo({ t, page: 'signin' }).label} ›
										</Link>
									</div>
								</div>
							</form>
						</AuthBlock>
						<div className={`grid select-none justify-center gap-4xl`}>
							<Link href={tradingViewPath} target={'_blank'} className={'grid gap-md text-center'}>
								<span className={'text-sm font-medium'}>{t('powered_by')}</span>
								<Image height={30} width={200} className={'mx-auto'} src={TradingViewImg} alt='TradingView' />
							</Link>
							<Link href={trustpilotTralentPath} target={'_blank'}>
								<Image height={50} width={250} className={'mx-auto'} src={TrustpilotImg} alt='Trustpilot' />
							</Link>
						</div>
					</>
				) : (
					<AuthBlock wrapProps={{ className: `animate-fadeIn` }}>
						<Block variant={'dark950'} size={'lg'} isChildrenWithPadding className={`text-center`}>
							<Image width={88} height={88} className={'mx-auto'} src={EmailSearch} alt='' />
							<div className={`mt-3xl grid gap-lg`}>
								<h4>{t('_signup_confirm.title')}</h4>
								<div className={`text-dark-300`}>
									{t('_signup_confirm.description')}
									<p>{watch('email')}</p>
								</div>
							</div>
						</Block>
						<div className={`mt-4xl grid gap-3xl sm:mt-7xl`}>
							<div className={`grid gap-lg text-sm text-dark-300`}>
								<p
									className={`text-white`}
									dangerouslySetInnerHTML={{ __html: t('_signup_confirm._instructions.text_1') }}
								/>
								<p dangerouslySetInnerHTML={{ __html: t('_signup_confirm._instructions.text_2') }} />
								<p dangerouslySetInnerHTML={{ __html: t('_signup_confirm._instructions.text_3') }} />
								<p dangerouslySetInnerHTML={{ __html: t('_signup_confirm._instructions.text_4') }} />
							</div>
							<div>
								{t('already_have_account')}{' '}
								<Link href={routeInfo({ t, page: 'signin' }).path} className={`link`}>
									{t('sign_in')} ›
								</Link>
							</div>
						</div>
					</AuthBlock>
				)}
			</div>
		</Layout>
	);
}
